import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Leaf, Zap, Flame, Droplets, Recycle, Factory, TreePine, Target, CloudSun,
  AlertTriangle, ArrowRight, Building2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AskEvaButton } from "@/components/AskEvaButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import ModuleTab from "@/components/environmental/ModuleTab";
import EmissionsTab from "@/components/environmental/EmissionsTab";

const ENERGY_CATEGORY_LABELS = {
  ELECTRICITY: "Elettricità acquistata", NATURAL_GAS: "Gas naturale", DIESEL: "Gasolio",
  PETROL: "Benzina", LPG: "GPL", HEAT_PURCHASED: "Calore acquistato",
  STEAM_PURCHASED: "Vapore acquistato", COOLING_PURCHASED: "Raffrescamento acquistato",
  PV_SELFPRODUCED: "Fotovoltaico autoprodotto", REFRIGERANT: "Refrigeranti", OTHER_FUEL: "Altro combustibile",
};

const MODULES = [
  { key: "ENERGIA", label: "Energia", icon: Zap, desc: "Consumi energetici per fonte e sede, con conversione automatica in MWh.", disclosure: "B3" },
  { key: "EMISSIONI", label: "Emissioni (Carbon Calculator)", icon: Flame, desc: "Scope 1 e Scope 2 location-based calcolati con fattori ufficiali versionati.", disclosure: "B3" },
  { key: "ACQUA", label: "Acqua", icon: Droplets, desc: "Prelievi, scarichi e consumo idrico per sede, con stress idrico.", disclosure: "B6" },
  { key: "RIFIUTI", label: "Rifiuti e circolarità", icon: Recycle, desc: "Rifiuti pericolosi e non, recupero e riciclo.", disclosure: "B7" },
  { key: "INQUINAMENTO", label: "Inquinamento", icon: Factory, desc: "Emissioni in aria, acqua e suolo già comunicate alle autorità.", disclosure: "B4" },
  { key: "BIODIVERSITA", label: "Biodiversità", icon: TreePine, desc: "Siti in o vicino ad aree sensibili per la biodiversità.", disclosure: "B5" },
  { key: "OBIETTIVI", label: "Obiettivi climatici", icon: Target, desc: "Target di riduzione GHG con baseline e avanzamento.", disclosure: "C3" },
  { key: "RISCHI", label: "Rischi climatici", icon: CloudSun, desc: "Rischi fisici e di transizione, orizzonti e misure.", disclosure: "C4" },
];

const ENERGY_CONFIG = {
  key: "energy-activities", addLabel: "Aggiungi consumo",
  description: "Inserisci i consumi come li leggi in bolletta o in fattura: il sistema converte le unità e calcola le emissioni.",
  columns: [
    { key: "energy_category", label: "Fonte", render: (r) => ENERGY_CATEGORY_LABELS[r.energy_category] || r.energy_category },
    { key: "quantity", label: "Quantità", render: (r) => `${r.quantity} ${r.original_unit}` },
    { key: "renewable_status", label: "Rinnovabilità" },
    { key: "supplier", label: "Fornitore" },
    { key: "counting_method", label: "Metodo" },
  ],
  fields: [
    { key: "energy_category", label: "Fonte energetica", type: "select", required: true,
      options: Object.entries(ENERGY_CATEGORY_LABELS).map(([value, label]) => ({ value, label })) },
    { key: "quantity", label: "Quantità", type: "number", required: true, hint: "Come da bolletta/fattura. Zero è un valore valido." },
    { key: "original_unit", label: "Unità", type: "select", required: true,
      options: ["kWh", "MWh", "GJ", "m3", "l", "kg", "t"].map((u) => ({ value: u, label: u })) },
    { key: "renewable_status", label: "Rinnovabilità", type: "select",
      options: [{ value: "RENEWABLE", label: "Rinnovabile" }, { value: "NON_RENEWABLE", label: "Non rinnovabile" }, { value: "MIXED", label: "Mista / non nota" }] },
    { key: "company_site_id", label: "Sede", type: "site" },
    { key: "counting_method", label: "Metodo di misura", type: "select",
      options: [{ value: "FUEL_BASED", label: "Consumo carburante (fatture)" }, { value: "DISTANCE_BASED", label: "Km percorsi" }, { value: "INVOICE", label: "Bolletta/fattura" }, { value: "METER", label: "Misuratore" }],
      hint: "Non inserire lo stesso consumo sia come carburante sia come km: rischio doppio conteggio." },
    { key: "supplier", label: "Fornitore" },
    { key: "invoice_reference", label: "Riferimento documento" },
    { key: "notes", label: "Note", type: "textarea", full: true },
  ],
};

const WATER_CONFIG = {
  key: "water-activities", addLabel: "Aggiungi prelievo idrico",
  description: "Prelievi e scarichi per sede. Il consumo è calcolato come prelievo meno scarico quando appropriato.",
  columns: [
    { key: "water_source", label: "Fonte" },
    { key: "withdrawal_quantity", label: "Prelievo (m³)" },
    { key: "discharge_quantity", label: "Scarico (m³)" },
    { key: "high_water_stress", label: "Area ad alto stress idrico" },
  ],
  fields: [
    { key: "company_site_id", label: "Sede", type: "site" },
    { key: "water_source", label: "Fonte", type: "select",
      options: [{ value: "RETE", label: "Rete idrica" }, { value: "POZZO", label: "Pozzo" }, { value: "SUPERFICIALE", label: "Acqua superficiale" }, { value: "ALTRO", label: "Altra fonte" }] },
    { key: "withdrawal_quantity", label: "Prelievo annuo (m³)", type: "number", required: true },
    { key: "discharge_quantity", label: "Scarico annuo (m³)", type: "number", hint: "Se noto. Non può superare il prelievo." },
    { key: "high_water_stress", label: "Area ad alto stress idrico", type: "select",
      options: [{ value: "YES", label: "Sì" }, { value: "NO", label: "No" }, { value: "TO_VERIFY", label: "Da verificare" }] },
    { key: "estimated", label: "Dato stimato", type: "boolean" },
    { key: "notes", label: "Note", type: "textarea", full: true },
  ],
};

const WASTE_CONFIG = {
  key: "waste-records", addLabel: "Aggiungi rifiuto",
  description: "Registra i rifiuti prodotti. Se non conosci la destinazione finale usa NON NOTO: non viene conteggiato come riciclato.",
  columns: [
    { key: "waste_code_or_category", label: "Codice/categoria" },
    { key: "description", label: "Descrizione" },
    { key: "hazardous", label: "Pericolosità", render: (r) => r.hazardous === "HAZARDOUS" ? "Pericoloso" : r.hazardous === "NON_HAZARDOUS" ? "Non pericoloso" : "—" },
    { key: "quantity", label: "Quantità", render: (r) => `${r.quantity} ${r.unit || "t"}` },
    { key: "destination", label: "Destinazione" },
    { key: "diverted_from_disposal", label: "Recuperato/riciclato", render: (r) => r.diverted_from_disposal ? "Sì" : "No" },
  ],
  fields: [
    { key: "waste_code_or_category", label: "Codice CER o categoria", required: true },
    { key: "description", label: "Descrizione", full: true },
    { key: "hazardous", label: "Pericolosità", type: "select", required: true,
      options: [{ value: "NON_HAZARDOUS", label: "Non pericoloso" }, { value: "HAZARDOUS", label: "Pericoloso" }] },
    { key: "quantity", label: "Quantità", type: "number", required: true },
    { key: "unit", label: "Unità", type: "select", options: [{ value: "t", label: "tonnellate" }, { value: "kg", label: "kg" }] },
    { key: "destination", label: "Destinazione finale", type: "select",
      options: [{ value: "RICICLO", label: "Riciclo" }, { value: "RECUPERO", label: "Recupero" }, { value: "SMALTIMENTO", label: "Smaltimento" }, { value: "NON_NOTO", label: "Non noto" }] },
    { key: "diverted_from_disposal", label: "Avviato a riciclo/riuso", type: "boolean" },
    { key: "company_site_id", label: "Sede", type: "site" },
  ],
};

const POLLUTION_CONFIG = {
  key: "pollution-records", addLabel: "Aggiungi inquinante",
  description: "Da compilare solo se l'azienda comunica già queste emissioni alle autorità competenti o tramite un sistema di gestione ambientale.",
  columns: [
    { key: "medium", label: "Mezzo" },
    { key: "pollutant", label: "Sostanza" },
    { key: "quantity", label: "Quantità", render: (r) => `${r.quantity} ${r.unit || ""}` },
    { key: "authority", label: "Autorità" },
    { key: "public_reference", label: "Riferimento pubblico" },
  ],
  fields: [
    { key: "medium", label: "Mezzo", type: "select", required: true,
      options: [{ value: "AIR", label: "Aria" }, { value: "WATER", label: "Acqua" }, { value: "SOIL", label: "Suolo" }] },
    { key: "pollutant", label: "Sostanza inquinante", required: true },
    { key: "quantity", label: "Quantità", type: "number", required: true },
    { key: "unit", label: "Unità" },
    { key: "authority", label: "Autorità competente" },
    { key: "public_reference", label: "URL / riferimento pubblico", full: true },
    { key: "notes", label: "Note", type: "textarea", full: true },
  ],
};

const MATERIAL_CONFIG = {
  key: "material-flows", addLabel: "Aggiungi flusso di materiali",
  description: "Per imprese con flussi materiali significativi (manifattura, costruzioni, packaging).",
  columns: [
    { key: "material_category", label: "Categoria" },
    { key: "material_name", label: "Materiale" },
    { key: "quantity", label: "Quantità", render: (r) => `${r.quantity} ${r.unit || ""}` },
    { key: "renewable_or_recycled_attribute", label: "Attributo" },
  ],
  fields: [
    { key: "material_category", label: "Categoria", required: true },
    { key: "material_name", label: "Materiale", required: true },
    { key: "quantity", label: "Quantità annua", type: "number", required: true },
    { key: "unit", label: "Unità", type: "select", options: [{ value: "t", label: "tonnellate" }, { value: "kg", label: "kg" }, { value: "m3", label: "m³" }] },
    { key: "renewable_or_recycled_attribute", label: "Attributo", type: "select",
      options: [{ value: "RENEWABLE", label: "Rinnovabile" }, { value: "RECYCLED", label: "Riciclato" }, { value: "VIRGIN", label: "Vergine" }] },
  ],
};

const TARGET_CONFIG = {
  key: "climate-targets", addLabel: "Aggiungi obiettivo GHG",
  description: "Obiettivi di riduzione delle emissioni con baseline e anno obiettivo (disclosure C3, modulo Comprehensive).",
  columns: [
    { key: "target_name", label: "Obiettivo" },
    { key: "scope", label: "Scope" },
    { key: "base_year", label: "Baseline", render: (r) => `${r.base_year}: ${r.base_year_value} ${r.unit || "tCO2e"}` },
    { key: "target_year", label: "Target", render: (r) => `${r.target_year}: ${r.target_year_value} ${r.unit || "tCO2e"}` },
    { key: "percentage_reduction", label: "Riduzione", render: (r) => r.percentage_reduction != null ? `-${r.percentage_reduction}%` : "—" },
  ],
  fields: [
    { key: "target_name", label: "Nome obiettivo", required: true, full: true },
    { key: "scope", label: "Scope", type: "select",
      options: [{ value: "SCOPE_1", label: "Scope 1" }, { value: "SCOPE_2", label: "Scope 2" }, { value: "SCOPE_1_2", label: "Scope 1+2" }] },
    { key: "base_year", label: "Anno baseline", type: "number", required: true },
    { key: "base_year_value", label: "Valore baseline (tCO2e)", type: "number", required: true },
    { key: "target_year", label: "Anno target", type: "number", required: true },
    { key: "target_year_value", label: "Valore target (tCO2e)", type: "number", required: true },
    { key: "percentage_reduction", label: "Riduzione prevista (%)", type: "number" },
    { key: "main_actions", label: "Azioni principali", type: "textarea", full: true,
      hint: "Es. efficientamento, fotovoltaico, elettrificazione, cambio combustibile." },
  ],
};

const RISK_CONFIG = {
  key: "climate-risks", addLabel: "Aggiungi rischio climatico",
  description: "Rischi climatici fisici e di transizione (disclosure C4, modulo Comprehensive). La valutazione dell'effetto economico non è una previsione finanziaria.",
  columns: [
    { key: "hazard_or_event", label: "Rischio" },
    { key: "physical_or_transition", label: "Tipo" },
    { key: "time_horizon", label: "Orizzonte" },
    { key: "financial_effect_level", label: "Effetto economico" },
    { key: "adaptation_action_exists", label: "Misure", render: (r) => r.adaptation_action_exists ? "Sì" : "No" },
  ],
  fields: [
    { key: "hazard_or_event", label: "Rischio / evento", required: true, full: true,
      hint: "Es. alluvioni, siccità, ondate di calore, policy/legal, mercato, tecnologia, reputazione." },
    { key: "physical_or_transition", label: "Tipologia", type: "select", required: true,
      options: [{ value: "PHYSICAL", label: "Fisico" }, { value: "TRANSITION", label: "Transizione" }] },
    { key: "acute_or_chronic", label: "Acuto/cronico", type: "select",
      options: [{ value: "ACUTE", label: "Acuto" }, { value: "CHRONIC", label: "Cronico" }] },
    { key: "time_horizon", label: "Orizzonte temporale", type: "select",
      options: [{ value: "SHORT_TERM", label: "Breve termine" }, { value: "MEDIUM_TERM", label: "Medio termine" }, { value: "LONG_TERM", label: "Lungo termine" }] },
    { key: "exposure_description", label: "Descrizione dell'esposizione", type: "textarea", full: true },
    { key: "adaptation_action_exists", label: "Esistono misure di adattamento/mitigazione", type: "boolean" },
    { key: "adaptation_action_description", label: "Descrizione delle misure", type: "textarea", full: true },
    { key: "financial_effect_level", label: "Livello effetto economico", type: "select",
      options: [{ value: "HIGH", label: "Alto" }, { value: "MEDIUM", label: "Medio" }, { value: "LOW", label: "Basso" }, { value: "NOT_ASSESSED", label: "Non valutato" }] },
  ],
};

function KpiCard({ label, value, unit, testid, sub }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-4">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-extrabold text-slate-900 mt-1">
          {value != null ? value : "—"} <span className="text-sm font-medium text-slate-500">{unit}</span>
        </p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function BiodiversityTab({ companyId, sites, canWrite, onChanged }) {
  const update = async (site, patch) => {
    try {
      await api.put(`/companies/${companyId}/sites/${site.id}`, { ...site, ...patch });
      toast.success("Sede aggiornata", { description: site.nome });
      onChanged();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };
  const STATUS = [
    { value: "NOT_IN_OR_NEAR", label: "Non in o vicino ad area sensibile" },
    { value: "IN_SENSITIVE_AREA", label: "Dentro area sensibile" },
    { value: "NEAR_SENSITIVE_AREA", label: "Vicino ad area sensibile" },
    { value: "TO_VERIFY", label: "Da verificare" },
  ];
  return (
    <div className="space-y-4" data-testid="tab-content-biodiversity">
      <p className="text-sm text-slate-600 leading-relaxed max-w-2xl">
        Per ogni sede indica la relazione con aree sensibili per la biodiversità (es. Natura 2000, aree protette).
        Senza una fonte geografica verificata, usa "Da verificare": il sistema non deduce il risultato dal solo indirizzo.
      </p>
      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid="table-biodiversity">
            <TableHeader>
              <TableRow>
                <TableHead>Sede</TableHead><TableHead>Città</TableHead>
                <TableHead>Stato biodiversità</TableHead><TableHead>Superficie (ha)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sites.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8" data-testid="biodiversity-empty">
                  Nessuna sede registrata. Aggiungi le sedi dall'anagrafica azienda.
                </TableCell></TableRow>
              )}
              {sites.map((s) => (
                <TableRow key={s.id} data-testid={`bio-site-${s.id.slice(0, 8)}`}>
                  <TableCell className="font-medium">{s.nome}</TableCell>
                  <TableCell>{s.citta || "—"}</TableCell>
                  <TableCell>
                    {canWrite ? (
                      <Select value={s.biodiversity_status || "TO_VERIFY"} onValueChange={(v) => update(s, { biodiversity_status: v })}>
                        <SelectTrigger className="w-[260px] h-8" data-testid={`bio-status-${s.id.slice(0, 8)}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline" className="bg-slate-50">{STATUS.find((o) => o.value === s.biodiversity_status)?.label || "Da verificare"}</Badge>
                    )}
                  </TableCell>
                  <TableCell>{s.area_ha ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <p className="text-xs text-slate-500">
        Per modificare la superficie della sede usa l'anagrafica azienda. I risultati alimentano automaticamente la disclosure VSME B5.
      </p>
    </div>
  );
}

export default function Environmental() {
  const { companyId, period, can, refreshPeriods } = useTenant();
  const [summary, setSummary] = useState(null);
  const [sites, setSites] = useState([]);
  const [tab, setTab] = useState("ENERGIA");
  const [calcKey, setCalcKey] = useState(0);
  const canWrite = can("esg:write");
  const comprehensiveActive = summary?.period?.reporting_option === "BASIC_COMPREHENSIVE";
  const visibleModules = comprehensiveActive
    ? MODULES
    : MODULES.filter((module) => !["C3", "C4"].includes(module.disclosure));

  const loadSites = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/sites`);
      setSites(data);
    } catch { setSites([]); }
  }, [companyId]);

  const load = useCallback(async () => {
    if (!companyId || !period?.id) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${period.id}/environmental/summary`);
      setSummary(data);
      setCalcKey((k) => k + 1);
    } catch (err) {
      toast.error("Errore caricamento Environmental", { description: formatApiError(err) });
    }
  }, [companyId, period?.id]);

  useEffect(() => { load(); loadSites(); }, [load, loadSites]);

  const onChanged = (s) => { if (s) setSummary(s); setCalcKey((k) => k + 1); };

  const g = summary?.ghg;
  const e = summary?.energy;
  const w = summary?.water;
  const wa = summary?.waste;
  const bio = summary?.biodiversity;

  return (
    <div className="space-y-6" data-testid="environmental-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Leaf className="w-7 h-7 text-green-700" aria-hidden="true" />
            Environmental
          </h1>
          <AskEvaButton question="Spiegami come è stato calcolato il valore di questa sezione." />
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Esercizio {period?.year}. Inserisci dati operativi: il motore calcola MWh, Scope 1, Scope 2 location-based,
            GHG totali e intensità, e aggiorna automaticamente i datapoint VSME B3–B7, C3, C4.
          </p>
        </div>
        <Badge variant="outline" className="bg-green-50 text-green-800 border-green-300" data-testid="env-method-badge">
          Metodo: GHG Protocol · Scope 2 location-based
        </Badge>
      </div>

      {summary?.alerts?.length > 0 && (
        <div className="space-y-1.5" data-testid="env-alerts">
          {summary.alerts.map((a) => (
            <p key={a.message} role="alert" className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3">
              <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" /> {a.message}
            </p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="env-kpi-strip">
        <KpiCard label="Energia totale" value={e?.total_mwh} unit="MWh" testid="kpi-energy"
          sub={e ? (e.breakdown_available ? `Rinnovabile: ${e.renewable_mwh} MWh` : "Breakdown rinnovabile non disponibile") : null} />
        <KpiCard label="Scope 1" value={g?.scope1_tco2e} unit="tCO₂e" testid="kpi-scope1" />
        <KpiCard label="Scope 2 (location-based)" value={g?.scope2_location_tco2e} unit="tCO₂e" testid="kpi-scope2" />
        <KpiCard label="GHG totali" value={g?.total_tco2e} unit="tCO₂e" testid="kpi-ghg-total"
          sub={g?.intensity_tco2e_per_meur != null ? `Intensità: ${g.intensity_tco2e_per_meur} tCO₂e/M€` : "Intensità: fatturato mancante"} />
        <KpiCard label="Prelievo idrico" value={w?.withdrawal_m3} unit="m³" testid="kpi-water"
          sub={w ? `Stress idrico: ${w.high_water_stress === "YES" ? "Sì" : w.high_water_stress === "TO_VERIFY" ? "Da verificare" : "No"}` : null} />
        <KpiCard label="Rifiuti totali" value={wa?.total_t} unit="t" testid="kpi-waste"
          sub={wa ? `Avviati a riciclo/riuso: ${wa.diverted_t} t` : null} />
        <KpiCard label="Siti in aree sensibili" value={bio?.sites_sensitive} unit={`su ${bio?.sites_total || 0}`} testid="kpi-biodiversity"
          sub={bio?.sites_to_verify ? `${bio.sites_to_verify} sedi da verificare` : null} />
        <KpiCard label="Scope 2 market-based" value={g?.scope2_market_tco2e} unit="tCO₂e" testid="kpi-scope2-market"
          sub="Metrica aggiuntiva: richiede evidenze contrattuali" />
      </div>

      {summary?.comparison && (
        <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="env-comparison">
          Rispetto al {summary.comparison.year}:{" "}
          energia {summary.comparison.energy_delta_pct != null ? `${summary.comparison.energy_delta_pct > 0 ? "+" : ""}${summary.comparison.energy_delta_pct}%` : "n.d."}
          {" · "}emissioni GHG {summary.comparison.ghg_delta_pct != null ? `${summary.comparison.ghg_delta_pct > 0 ? "+" : ""}${summary.comparison.ghg_delta_pct}%` : "n.d."}
          {" "}<span className="text-slate-500">(variazione quantitativa, senza valutazione automatica)</span>
        </p>
      )}

      {!comprehensiveActive && (
        <div
          className="border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"
          data-testid="environmental-comprehensive-inactive"
        >
          Comprehensive Module non attivo – i datapoint C1–C9 sono fuori dal perimetro di
          rendicontazione.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="env-modules">
        {visibleModules.map((m) => {
          const stats = summary?.modules?.[m.key];
          return (
            <button key={m.key} type="button" onClick={() => setTab(m.key)} data-testid={`module-card-${m.key.toLowerCase()}`}
              className={`text-left rounded-lg border p-4 transition-colors ${tab === m.key ? "border-green-600 bg-green-50" : "border-slate-200 bg-white hover:border-green-400"}`}>
              <div className="flex items-center gap-2 mb-1">
                <m.icon className="w-4 h-4 text-green-700" aria-hidden="true" />
                <span className="text-sm font-semibold text-slate-900">{m.label}</span>
              </div>
              <p className="text-xs text-slate-600 leading-snug mb-2">{m.desc}</p>
              {stats && (
                <p className="text-xs font-semibold text-slate-700" data-testid={`module-completion-${m.key.toLowerCase()}`}>
                  VSME {m.disclosure}: {stats.completion}% {stats.not_applicable > 0 && stats.applicable === 0 ? "— non applicabile" : ""}
                </p>
              )}
            </button>
          );
        })}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto" data-testid="env-tabs">
          {visibleModules.map((m) => (
            <TabsTrigger key={m.key} value={m.key} data-testid={`tab-${m.key.toLowerCase()}`}>{m.label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="ENERGIA">
          <ModuleTab companyId={companyId} periodId={period?.id} config={ENERGY_CONFIG} sites={sites} canWrite={canWrite} onChanged={onChanged} registersSummary={calcKey} />
        </TabsContent>
        <TabsContent value="EMISSIONI">
          <EmissionsTab companyId={companyId} periodId={period?.id} refreshKey={calcKey} />
        </TabsContent>
        <TabsContent value="ACQUA">
          <ModuleTab companyId={companyId} periodId={period?.id} config={WATER_CONFIG} sites={sites} canWrite={canWrite} onChanged={onChanged} registersSummary={calcKey} />
        </TabsContent>
        <TabsContent value="RIFIUTI">
          <ModuleTab companyId={companyId} periodId={period?.id} config={WASTE_CONFIG} sites={sites} canWrite={canWrite} onChanged={onChanged} registersSummary={calcKey} />
        </TabsContent>
        <TabsContent value="INQUINAMENTO">
          <ModuleTab companyId={companyId} periodId={period?.id} config={POLLUTION_CONFIG} sites={sites} canWrite={canWrite} onChanged={onChanged} registersSummary={calcKey} />
        </TabsContent>
        <TabsContent value="BIODIVERSITA">
          <BiodiversityTab companyId={companyId} sites={sites} canWrite={canWrite}
            onChanged={() => { loadSites(); load(); }} />
        </TabsContent>
        <TabsContent value="MATERIALI">
          <ModuleTab companyId={companyId} periodId={period?.id} config={MATERIAL_CONFIG} sites={sites} canWrite={canWrite} onChanged={onChanged} registersSummary={calcKey} />
        </TabsContent>
        <TabsContent value="OBIETTIVI">
          <ModuleTab companyId={companyId} periodId={period?.id} config={TARGET_CONFIG} sites={sites} canWrite={canWrite} onChanged={onChanged} registersSummary={calcKey} />
        </TabsContent>
        <TabsContent value="RISCHI">
          <ModuleTab companyId={companyId} periodId={period?.id} config={RISK_CONFIG} sites={sites} canWrite={canWrite} onChanged={onChanged} registersSummary={calcKey} />
        </TabsContent>
      </Tabs>

      <p className="text-xs text-slate-500" data-testid="env-footer-note">
        I risultati calcolati alimentano i datapoint VSME (source: CALCULATED) senza sostituire valori verificati o approvati.
        Materiali: modulo attivo solo per settori con flussi materiali significativi.
      </p>
    </div>
  );
}
